import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { toast } from 'sonner';

interface RealtimeTournamentOptions {
  onTournamentUpdate?: (payload: any) => void;
  onPlayerJoin?: (payload: any) => void;
  onResultsUpdate?: (payload: any) => void;
}

export const useRealtimeTournament = (options: RealtimeTournamentOptions = {}) => {
  const { user } = useAuth();
  const { onTournamentUpdate, onPlayerJoin, onResultsUpdate } = options;

  const handleTournamentStateUpdate = useCallback((payload: any) => {
    const { eventType, new: newData, old: oldData } = payload;
    
    if (eventType === 'UPDATE' && newData) {
      onTournamentUpdate?.(payload);
      
      // Show notification for tournament updates
      const tournamentState = JSON.parse(newData.state || '{}');
      if (tournamentState.isActive && tournamentState.currentRound !== JSON.parse(oldData?.state || '{}').currentRound) {
        toast.success(`Round ${tournamentState.currentRound} has started!`);
      }
    }
  }, [onTournamentUpdate]);

  const handlePlayerUpdate = useCallback((payload: any) => {
    const { eventType, new: newData } = payload;
    
    if (eventType === 'INSERT' && newData) {
      onPlayerJoin?.(payload);
      
      toast.success(`${newData.name || 'A player'} joined the tournament`);
    }
  }, [onPlayerJoin]);

  const handleMatchUpdate = useCallback((payload: any) => {
    const { eventType, new: newData } = payload;
    
    if (eventType === 'INSERT' && newData) {
      onResultsUpdate?.(payload);
      
      toast.success(`Round ${newData.round_number} results updated`);
    }
  }, [onResultsUpdate]);

  useEffect(() => {
    if (!user) return;

    // Subscribe to tournament state changes
    const tournamentChannel = supabase
      .channel('tournament-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tournaments_state',
          filter: `user_id=eq.${user.id}`,
        },
        handleTournamentStateUpdate
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'players',
        },
        handlePlayerUpdate
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'matches',
        },
        handleMatchUpdate
      )
      .subscribe();

    return () => {
      supabase.removeChannel(tournamentChannel);
    };
  }, [user, handleTournamentStateUpdate, handlePlayerUpdate, handleMatchUpdate]);

  // Enable realtime for tournament tables
  const enableRealtime = useCallback(async () => {
    try {
      await supabase.rpc('alter table tournaments_state replica identity full' as any);
      await supabase.rpc('alter table players replica identity full' as any);
      await supabase.rpc('alter table matches replica identity full' as any);
    } catch (error) {
      console.log('Realtime already enabled or permission denied:', error);
    }
  }, []);

  return {
    enableRealtime,
  };
};